import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import {
  KoobButton,
  KoobInputCheckbox,
  KoobInputTextareaField,
  KoobModal,
} from '@koob/ui';
import { formatDate } from '@koob/dates';
import BookingStatusBadge from '../BookingStatusBadge';

export interface FolderCancellableBooking {
  id: string | number;
  state: string;
  startAt?: string;
  endAt?: string;
  hotel?: { displayName?: string; city?: { title?: string } };
  experiencesBooked?: {
    experience?: { name?: string; inCity?: { title?: string } };
  }[];
}

export interface FolderCancelAllBookingsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The bookings offered, from getCancellableFolderBookings. */
  bookings: FolderCancellableBooking[];
  onSubmit: (values: {
    bookingIds: FolderCancellableBooking['id'][];
    reason: string;
  }) => Promise<void> | void;
}

/**
 * KD-3842 "Cancel all bookings" confirmation: pick the bookings, accept the
 * cancellation fees and give a reason. Each app sends the cancellation.
 */
export default function FolderCancelAllBookingsModal({
  open,
  setOpen,
  bookings,
  onSubmit,
}: FolderCancelAllBookingsModalProps) {
  const { t } = useTranslation('folders');
  const [selectedIds, setSelectedIds] = useState<
    FolderCancellableBooking['id'][]
  >([]);
  const [agreeToFees, setAgreeToFees] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);

  // Each opening selects every booking again and unchecks the fees; only the
  // opening, as the app rebuilds `bookings` on every render.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSelectedIds(bookings.map((booking) => booking.id));
      setAgreeToFees(false);
    }
  }

  const allSelected = selectedIds.length === bookings.length;

  const toggleBooking = (bookingId: FolderCancellableBooking['id']) =>
    setSelectedIds((current) =>
      current.includes(bookingId)
        ? current.filter((id) => id !== bookingId)
        : [...current, bookingId],
    );

  const bookingLabel = (booking: FolderCancellableBooking) => {
    if (booking.hotel) {
      return `${booking.hotel.displayName} - ${booking.hotel.city?.title}`;
    }
    const experience = booking.experiencesBooked?.[0]?.experience;
    return experience
      ? `${experience.name} - ${experience.inCity?.title}`
      : t('cancelAll.unknownBooking');
  };

  return (
    <KoobModal
      open={open}
      setOpen={setOpen}
      title={t('misc:warning')}
      widthClass="sm:max-w-xl"
    >
      <p className="m-0 mb-5 text-sm text-gray-700">
        {t('cancelAll.warningMessage')}
      </p>

      <Formik
        initialValues={{ reason: '' }}
        validate={({ reason }) =>
          reason.trim() ? {} : { reason: t('errors:required') }
        }
        onSubmit={({ reason }) => onSubmit({ bookingIds: selectedIds, reason })}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <div className="mb-4 max-h-[300px] overflow-y-auto rounded-lg border border-solid border-gray-200 p-4">
              <div className="mb-2.5 flex items-center justify-between border-0 border-b border-solid border-gray-200 pb-2.5">
                <strong>
                  {t('cancelAll.bookingsToCancel')} ({selectedIds.length}/
                  {bookings.length})
                </strong>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedIds(
                      allSelected ? [] : bookings.map((booking) => booking.id),
                    )
                  }
                  className="cursor-pointer border-none bg-transparent text-brand underline"
                >
                  {allSelected
                    ? t('cancelAll.deselectAll')
                    : t('cancelAll.selectAll')}
                </button>
              </div>

              <div className="flex flex-col gap-2.5">
                {bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className={`cursor-pointer select-none rounded p-2 ${
                      selectedIds.includes(booking.id)
                        ? 'ring-2 ring-k-orange'
                        : ''
                    }`}
                    onClick={() => toggleBooking(booking.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <KoobInputCheckbox
                          checked={selectedIds.includes(booking.id)}
                        />
                        <div>
                          <div className="font-medium">
                            {bookingLabel(booking)}
                          </div>
                          {booking.startAt && booking.endAt && (
                            <div className="mt-1 text-xs text-gray-400">
                              {formatDate(booking.startAt)} -{' '}
                              {formatDate(booking.endAt)}
                            </div>
                          )}
                        </div>
                      </div>
                      <BookingStatusBadge status={booking.state} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              onClick={() => setAgreeToFees(!agreeToFees)}
              className="mb-4 flex cursor-pointer items-center gap-2.5 rounded-lg border border-solid border-gray-200 px-5 py-2.5"
            >
              <KoobInputCheckbox
                checked={agreeToFees}
                data-testid="agree-cancellation-fees"
              />
              <span>{t('cancelAll.agreeToFees')}</span>
            </div>

            <KoobInputTextareaField
              name="reason"
              label={t('cancelAll.reason')}
              placeholder={t('cancelAll.reasonPlaceholder')}
            />

            <div className="flex justify-end gap-2.5 pt-4">
              <KoobButton
                type="button"
                variant="secondary"
                onClick={() => setOpen(false)}
              >
                {t('misc:cancel')}
              </KoobButton>
              <KoobButton
                variant="danger"
                loading={isSubmitting}
                type="submit"
                disabled={
                  !agreeToFees ||
                  !values.reason.trim() ||
                  selectedIds.length === 0
                }
                data-testid="cancel-all-bookings-confirm"
              >
                {t('cancelAll.continue', { count: selectedIds.length })}
              </KoobButton>
            </div>
          </Form>
        )}
      </Formik>
    </KoobModal>
  );
}
